import type {
  CurrentTvShow,
  TvShowEpisode,
  TvShowSeason,
} from '@setplex/tria-api'
import type { TFunction } from '~/core/i18n'
import type {
  AdjustPurchaseableFn,
  SseIds,
} from '~/features/payment/sse-purchases'
import type { ITvShowEpisodeFormat } from './index.h'

import { formatSeasonCountLabel } from '~/entities/shows/lib'
import {
  checkBundlePurchase,
  checkUpdatedItemIds,
  extendMediaItemByLabelPriceTvShowEpisode,
  getProgress,
} from '~/shared/tools'
import {
  formatEpisodeInfoLength,
  formatEpisodeInfoReleaseTime,
} from '~/widgets/Popups/InfoPopup/lib/episode'

export const MAX_PROGRESS_IN_PERCENTS = 100

export const isTvShowEpisodeLocked = ({
  episode,
  isLocked,
  unlockedId,
  tvShowId,
}: {
  episode: { id?: number; locked?: boolean }
  isLocked: (content?: { id?: number; locked?: boolean } | null) => boolean
  unlockedId: number | null
  tvShowId?: number
}) => tvShowId !== unlockedId && isLocked(episode)

export const formatProgress = ({
  duration,
  stoppedTime,
  watched,
}: {
  duration?: number | null
  stoppedTime?: number | null
  watched?: boolean
}): number | null => {
  if (!stoppedTime && watched) return MAX_PROGRESS_IN_PERCENTS

  return getProgress({
    duration,
    stoppedTime,
  })
}

export const formatTVShowEpisode = (
  episodes: TvShowEpisode[],
  adjust: AdjustPurchaseableFn,
  { t }: { t: TFunction },
  season?: TvShowSeason | null
): ITvShowEpisodeFormat[] => {
  return episodes.map((episode) => {
    const {
      duration,
      stoppedTime,
      watched,
      sortOrder,
      displayNumber,
      releaseTime,
    } = episode

    const count = Number(sortOrder) + 1
    const formattedSortOrder = t('episode_number', {
      number: count,
    })

    let seasonEpisodeShortLabel = ''
    if (displayNumber) {
      seasonEpisodeShortLabel = t('episode_shortened', {
        number: displayNumber,
      })
    }
    if (season?.displayNumber && displayNumber) {
      seasonEpisodeShortLabel = t('season_episode_shortened', {
        season_number: season.displayNumber,
        episode_number: displayNumber,
      })
    }

    const releaseYear = releaseTime
      ? new Date(releaseTime * 1000).getFullYear()
      : null

    const { rented, labelPrice } = extendMediaItemByLabelPriceTvShowEpisode(
      adjust(episode)
    )
    const progress = formatProgress({ duration, stoppedTime, watched })
    const formattedDuration =
      formatEpisodeInfoLength(episode, { short: true }) ?? ''
    const releaseTimeFormatted = formatEpisodeInfoReleaseTime(episode) ?? ''

    return {
      ...episode,
      releaseTimeFormatted,
      releaseYear,
      length: formattedDuration,
      formattedSortOrder,
      seasonEpisodeShortLabel,
      rented,
      labelPrice,
      progress,
    }
  })
}

export const formatTVShowSeasonEpisodesLabel = (
  episodes: ITvShowEpisodeFormat[] | null,
  { t }: { t: TFunction }
) => {
  const count = episodes?.length

  return count
    ? t('episode_count', {
        count,
      })
    : ''
}

export const formatTVShowEpisodesLabel = (
  tvShow: CurrentTvShow | null,
  { t }: { t: TFunction }
) => {
  return tvShow?.episodeCount
    ? t('episode_count', {
        count: tvShow.episodeCount,
      })
    : ''
}

export const formatTVShowSeasonsLabel = (
  tvShow: CurrentTvShow | null,
  { t }: { t: TFunction }
) => formatSeasonCountLabel(tvShow?.seasonCount, t)

/** The "Season" word beside the numbered chips, reused from `season_number`. */
export const formatSeasonSelectorLabel = ({ t }: { t: TFunction }) =>
  t('season_number', { number: '' }).replace(/\s+/g, ' ').trim()

export { seasonDetailsPath } from '~/shared/helpers'

export const checkUpdatedItem = ({
  currentShow,
  season,
  ids,
}: {
  currentShow: CurrentTvShow | null
  season: TvShowSeason | null
  ids: SseIds
}) => {
  const checkUpdatedItem = checkUpdatedItemIds(ids)

  return checkUpdatedItem(season) || checkUpdatedItem(currentShow)
}

/**
 * True when details `init` is only switching seasons on an already loaded show.
 * Source the API stores (`$currentTvShow`, `$tvShowSeason`) — `$season` and
 * `$initParams` are updated in the same `init` tick, so a sample must not read them.
 */
export const isSameShowSeasonSwitch = ({
  currentShowId,
  currentSeasonId,
  nextShowId,
  nextSeasonId,
}: {
  currentShowId?: number | null
  currentSeasonId?: number | null
  nextShowId: string
  nextSeasonId?: string | null
}): boolean => {
  if (currentShowId == null) return false
  if (currentShowId !== Number(nextShowId)) return false
  if (nextSeasonId == null || nextSeasonId === '') return false
  const nextId = Number(nextSeasonId)
  if (Number.isNaN(nextId)) return false
  return currentSeasonId !== nextId
}

export const checkUpdatedItemBundleIds = ({
  currentShow,
  season,
  ids,
}: {
  currentShow: CurrentTvShow | null
  season: TvShowSeason | null
  ids: SseIds
}) => {
  const currentShowUpdated = checkBundlePurchase({
    item: currentShow,
    priceSettings: currentShow?.priceSettings || [],
    bundleIds: ids,
  })

  const seasonUpdated = checkBundlePurchase({
    item: season,
    priceSettings: [
      ...(season?.tvShowPriceSettings || []),
      ...(season?.seasonPriceSettings || []),
    ],
    bundleIds: ids,
  })

  return currentShowUpdated || seasonUpdated
}
